import { configureStore } from "@reduxjs/toolkit";

import { apiErrorHandler } from "./middleware";
import { accountService } from "./services/account";
import { authService } from "./services/authentication";
import { configService } from "./services/config";
import { featureFlagReducer } from "./services/featureFlag/reducers";
import { moduleService } from "./services/modules";
import { userService } from "./services/user";
import { userInfoService } from "./services/userInfo";
import { venueService } from "./services/venues";
import { intercomReducer } from "./services/intercom";
import { statusService } from "./services/status";

// DE chat services
import {
  conversationService,
  conversationSlice,
  conversationStatusListener,
} from "../features/data-explorer/store/services/conversation";
import { accountFilesService } from "../features/data-explorer/store/services/accountFiles";
import { dashboardService as deDashboardService } from "../features/data-explorer/store/services/dashboard";
import { datasetService } from "../features/data-explorer/store/services/datasets";
import { chatSlice } from "../features/data-explorer/store/services/chat/chatSlice";
// DE almanac services (used by MessageRenderer tools)
import {
  itemService,
  calendarService,
  itemTypeService,
  venueService as deVenueService,
} from "../features/data-explorer/store/services/almanac";
// DE users service (separate from nav's userService)
import { usersService as deUsersService } from "../features/data-explorer/store/services/users";
// DE /v2/data/api services
import { clientCredentialsService } from "../features/data-explorer/store/services/clientCredentials/api";
import { queryHistoryService } from "../features/data-explorer/store/services/queryHistory";

export const store = configureStore({
  reducer: {
    [configService.reducerPath]: configService.reducer,
    [userInfoService.reducerPath]: userInfoService.reducer,
    [authService.reducerPath]: authService.reducer,
    [moduleService.reducerPath]: moduleService.reducer,
    [userService.reducerPath]: userService.reducer,
    [accountService.reducerPath]: accountService.reducer,
    [venueService.reducerPath]: venueService.reducer,
    [statusService.reducerPath]: statusService.reducer,
    featureFlag: featureFlagReducer,
    intercom: intercomReducer,
    // DE services
    [conversationService.reducerPath]: conversationService.reducer,
    [accountFilesService.reducerPath]: accountFilesService.reducer,
    [deDashboardService.reducerPath]: deDashboardService.reducer,
    [datasetService.reducerPath]: datasetService.reducer,
    [deUsersService.reducerPath]: deUsersService.reducer,
    [itemService.reducerPath]: itemService.reducer,
    [calendarService.reducerPath]: calendarService.reducer,
    [itemTypeService.reducerPath]: itemTypeService.reducer,
    [deVenueService.reducerPath]: deVenueService.reducer,
    [clientCredentialsService.reducerPath]: clientCredentialsService.reducer,
    [queryHistoryService.reducerPath]: queryHistoryService.reducer,
    // DE slices
    chat: chatSlice.reducer,
    conversationStatus: conversationSlice.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      apiErrorHandler,
      authService.middleware,
      configService.middleware,
      userInfoService.middleware,
      moduleService.middleware,
      userService.middleware,
      accountService.middleware,
      venueService.middleware,
      statusService.middleware,
      // DE middleware
      conversationService.middleware,
      accountFilesService.middleware,
      deDashboardService.middleware,
      datasetService.middleware,
      deUsersService.middleware,
      itemService.middleware,
      calendarService.middleware,
      itemTypeService.middleware,
      deVenueService.middleware,
      clientCredentialsService.middleware,
      queryHistoryService.middleware,
      conversationStatusListener.middleware,
    ),
  devTools: import.meta.env.MODE !== "production",
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
